import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import {
  PayPalButtons,
  FUNDING,
  PayPalScriptProvider,
} from '@paypal/react-paypal-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import checkoutService from '../../../api/checkoutService';
import {
  setAvailableShippingMethods,
  setCheckoutOrderID,
  setDeclineReasonCode,
  setIsDeclinePopup,
  setPurchaseToken,
} from '../../../redux/slices/checkoutSlice';
import {
  PayPalOrder,
  SetShippingAddressPayload,
} from '../../../interface/apiServiceInterface';
import { CheckoutCompletePayload } from '../../../interface/apiServiceInterface';

import { EventPublisher } from '../../../utils/publishEvent';
import { handleEventLogger } from '../../../utils/conversionAPI';
import { v4 as uuidv4 } from 'uuid';
import {
  getProvinceCode,
  updateContactInfo,
  updateShippingAddress,
} from '../applePay-button/paymentHelper';
import {
  Address,
  Cart,
  ShippingMethod,
} from '../../../interface/checkoutInterface';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setCheckoutErrorMessage,
  setIsPaymentByPaypal,
  setPurchaseCompleted,
  setSubmitSuccess,
} from '../../../redux/slices/commonSlice';
import { setCartData, setSalesTax } from '../../../redux/slices/cartSlice';
import { resetState } from '../../../redux/slices/postUpsellStateSlice';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  storePhoneCode,
} from '../../../utils/helper';
import { postUpsellPage } from '../../../utils/postUpsell';
import { getIsViewIsEdit, PageType } from '../../../utils/constants';
import { WidthAndHeightSubProps } from '../../../puck/reusable-props/widthAndHeight';
import { IBorderValueProps } from '../../../puck/reusable-props/Border';
import { MarginType } from '../../../puck/reusable-props/margin';
import { PaddingType } from '../../../puck/reusable-props/padding';
import '../../text-editor/style.scss';
import PayPal from '../../../../assets/svg/paypal';
import { useFormContext } from 'react-hook-form';
import { AddressFormType } from '../../../interface/formInterface';

export type PayPalColor = 'gold' | 'silver' | 'black' | 'white' | 'blue';

export interface PayPalButtonComponentProps
  extends ReduxSelectorProps,
    ReduxDispatchProps {
  background: {
    color: string;
    isInherit: boolean;
  };
  buttonCustomization: WidthAndHeightSubProps;
  headerText: string;
  showOnlyButton: boolean;
  buttonColor: PayPalColor;
  justifyContent: string;
  border: IBorderValueProps;
  TermsAndConCheck: { isTermsAndConChecked: boolean; errorMsg: string };
  editMode: boolean;
  margin: MarginType;
  padding: PaddingType;
}

const PaypalButton = ({
  useAppDispatch,
  useAppSelector,
  background,
  buttonCustomization,
  justifyContent,
  border,
  buttonColor,
  headerText,
  showOnlyButton,
  TermsAndConCheck,
  editMode,
  margin,
  padding,
}: PayPalButtonComponentProps) => {
  const publish = EventPublisher.getInstance().publish;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setValue } = useFormContext<AddressFormType>();
  const setCartDataRef = useRef<Cart | null>(null);
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { cartData } = useAppSelector(state => state.cart);
  const { currentPageFunnelId, pageId } = useAppSelector(
    state => state.checkout,
  );
  const { storeData } = useAppSelector(state => state?.store);
  const postUpSellProducts = useAppSelector(
    state => state.postUpSell.postUpsellData,
  );
  const initialOptions = {
    clientId: storeData?.PaypalClientId || '',
    currency: cartData?.currencyCode || 'USD',
    intent: 'capture',
  };
  const upsellData = useAppSelector(state => state.postUpSell.upsellData);
  const [termsError, setTermsError] = useState<string | null>(null);
  const newShopUrl = storeData?.ShopURL?.replace('https://', '');
  const redirectUrl = `/confirmation-page?store=${newShopUrl}`;
  const postUpsellRedirectUrl = `/post-upsell-page?store=${newShopUrl}`;
  const eventToken = searchParams.get('event-token');
  const NextPageConfig = useAppSelector(state => state.checkout.nextpageConfig);
  const { isTermsAccepted, IsSMSConsent, IsEmailConsent } = useAppSelector(
    state => state.common,
  );
  const smartFunnelId = useAppSelector(
    state => state.postUpSell.smartUpsellFunnelID,
  );
  const isCartApiLoading = useAppSelector(state => state.cart.isCartApiLoading);
  const newSearchParams = new URLSearchParams(searchParams.toString());
  const getAlignmentClass = (alignment: string) => {
    switch (alignment) {
      case 'left':
        return 'text-left';
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-center';
    }
  };
  const navState = useMemo(() => {
    if (!getIsViewIsEdit() && NextPageConfig?.id) {
      return {
        state: {
          id: NextPageConfig.id,
          pageVisiblity: NextPageConfig.pageVisiblity,
          pageType: NextPageConfig.pageType,
          smartFunnelId:
            NextPageConfig.pageType === PageType?.UPSELL ? smartFunnelId : '',
        },
        replace: true,
      };
    }
    return {};
  }, [
    NextPageConfig?.id,
    NextPageConfig?.pageVisiblity,
    NextPageConfig?.pageType,
    getIsViewIsEdit,
  ]);

  const handleNavigate = () => {
    navigate('/' + '?' + newSearchParams.toString(), navState);
  };

  const onCreateOrder = async (_data: unknown, actions: unknown) => {
    const paypalActions = actions as {
      order: { create: (_options: unknown) => Promise<string> };
    };
    try {
      return await paypalActions.order.create({
        purchase_units: [
          {
            amount: {
              value: cartData?.TotalAmount,
            },
          },
        ],
        application_context: {
          shipping_preference: 'GET_FROM_FILE',
        },
      });
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const onApproveOrder = async (_data: unknown, actions: unknown) => {
    try {
      const paypalActions = actions as {
        order?: { capture: () => Promise<PayPalOrder> };
      };
      if (!paypalActions.order) {
        throw new Error('PayPal order action is missing');
      }

      const details: PayPalOrder = await paypalActions.order.capture();
      await completeApi(details);
    } catch (err) {
      console.error('PayPal capture error:', err);
    }
  };

  const onShippingChange = async (data: unknown, actions: unknown) => {
    try {
      if (!cartData || !storeData) {
        throw new Error('Cart or Store data missing');
      }
      const shippingData = data as {
        shippingAddress?: Record<string, string>;
        orderID?: string;
      };
      const shipping = shippingData?.shippingAddress;
      const provinceCode = getProvinceCode({
        countryCode: shipping?.countryCode || '',
        administrativeArea: shipping?.state || '',
      });
      const Address = {
        FirstName: '',
        LastName: '',
        Address1: '',
        Address2: '',
        Organization: '',
        City: shipping?.city || '',
        CountryCode: shipping?.countryCode || '',
        ProvinceCode: provinceCode || shipping?.state || '',
        Zip: shipping?.postalCode || '',
      };

      const shippingPayload: SetShippingAddressPayload = {
        Address,
        ID: cartData.ID,
        ShopID: storeData.ShopID,
        StoreID: Number(storeData.StoreID) || 0,
        PaypalOrderID: shippingData?.orderID || '',
      };

      const response = await updateShippingAddress(
        shippingPayload,
        storeData.ShopID,
        setCartDataRef,
      );
      if (!response) return;
      setValue(`Shipping`, Address);
      dispatch(
        setAvailableShippingMethods(
          response?.AvailableShippingMethods as ShippingMethod[],
        ),
      );
      dispatch(
        setSalesTax({
          SalesTaxAmount: response?.SalesTaxAmount,
          TotalAmount: response?.TotalAmount,
          EstimatedTaxAmount: response?.EstimatedTaxAmount,
          Fees: response?.Fees,
          DiscountAmount: response?.DiscountAmount,
          SubtotalAmount: response?.SubtotalAmount,
          ShippingRequired: response?.ShippingRequired,
          ShippingMethodID: response?.ShippingMethodID,
        }),
      );
      return;
    } catch (error) {
      console.error('Error in onShippingChange:', error);

      const paypalActions = actions as { reject?: () => void };
      return paypalActions.reject?.();
    }
  };

  const completeApi = async (input: PayPalOrder) => {
    dispatch(setCheckoutErrorMessage(null));
    dispatch(setIsDeclinePopup(false));
    if (!cartData?.ID || !storeData?.StoreID || !storeData?.ShopID) return;
    if (!getIsViewIsEdit()) {
      publish('PurchaseAttempt', {
        eventId: uuidv4(),
        eventName: 'PurchaseAttempt',
        shopId: storeData?.ShopID,
        cart: cartData,
      });
      publish('AddPaymentInfo', {
        eventId: uuidv4(),
        eventName: 'AddPaymentInfo',
        shopId: storeData?.ShopID,
        cart: cartData,
      });
    }
    dispatch(setSubmitSuccess(true));
    const shipping = input?.purchase_units?.[0]?.shipping;
    const payer = input?.payer;
    const contactPayload = {
      Email: payer?.email_address || '',
      PhoneNumber: '',
      ID: cartData.ID,
      ShopID: storeData.ShopID,
      StoreID: Number(storeData.StoreID) || 0,
    };
    await updateContactInfo(contactPayload, storeData.ShopID);

    const shippingPayload: SetShippingAddressPayload = {
      Address: {
        FirstName: payer?.name?.given_name || '',
        LastName: payer?.name?.surname || '',
        Address1: shipping?.address?.address_line_1 || '',
        Address2: '',
        Organization: '',
        City: shipping?.address?.admin_area_2 || '',
        CountryCode: shipping?.address?.country_code || '',
        ProvinceCode: shipping?.address?.admin_area_1 || '',
        Zip: shipping?.address?.postal_code || '',
      },
      ID: cartData.ID,
      ShopID: storeData.ShopID,
      StoreID: Number(storeData.StoreID) || 0,
    };

    await updateShippingAddress(
      shippingPayload,
      storeData.ShopID,
      setCartDataRef,
    );

    const BillingAddress: Address = {
      Address1: shipping?.address?.address_line_1 || '',
      Address2: '',
      City: shipping?.address?.admin_area_2 || '',
      CountryCode: shipping?.address?.country_code || '',
      FirstName: payer?.name?.given_name || '',
      LastName: payer?.name?.surname || '',
      Organization: '',
      ProvinceCode: shipping?.address?.admin_area_1 || '',
      Zip: shipping?.address?.postal_code || '',
    };
    const ResponseString = JSON.stringify(input);
    const requestPayload = {
      purchase_units: [
        {
          amount: {
            value: input?.purchase_units?.[0]?.amount?.value || '0.00',
            currency_code:
              input?.purchase_units?.[0]?.amount?.currency_code || 'USD',
          },
        },
      ],
      application_context: {
        shipping_preference: 'GET_FROM_FILE',
      },
    };

    const request_string = JSON.stringify(requestPayload);

    const payload: CheckoutCompletePayload = {
      BillingAddress,
      ID: cartData.ID,
      IdempotencyKey: input?.id,
      ShopID: storeData.ShopID,
      IsTermsChecked: isTermsAccepted,
      IsSMSConsent: IsSMSConsent,
      IsEmailConsent: IsEmailConsent,
      StoreID: Number(storeData.StoreID) || 0,
      paypalPayload: {
        orderId: input?.id,
        transactionId:
          input?.purchase_units?.[0]?.payments?.captures?.[0]?.id || '',
        status: input?.status,
        email: payer?.email_address || '',
        request_string: request_string,
        ResponseString: ResponseString,
      },
      CurrentPageFunnelId: currentPageFunnelId || '',
      PageId: pageId || null,
    };
    const updatedCart = {
      ...cartData,
      Email: setCartDataRef.current?.Email || cartData.Email,
      ShippingAddress:
        setCartDataRef.current?.ShippingAddress || cartData.ShippingAddress,
      PhoneNumber: setCartDataRef.current?.PhoneNumber || cartData.PhoneNumber,
      BillingAddress: payload.BillingAddress,
      TotalAmount: setCartDataRef.current?.TotalAmount || cartData.TotalAmount,
      Fees: setCartDataRef.current?.Fees || cartData.Fees,
      SubtotalAmount:
        setCartDataRef.current?.SubtotalAmount || cartData.SubtotalAmount,
      SalesTaxAmount:
        setCartDataRef.current?.SalesTaxAmount || cartData.SalesTaxAmount,
    };
    try {
      const uniqueEventId = uuidv4();
      setLoading(true);
      const res = await checkoutService.checkoutComplete(
        payload,
        uniqueEventId,
      );
      if (!getIsViewIsEdit()) {
        publish('Purchase', {
          eventId: uniqueEventId,
          eventName: 'Purchase',
          shopId: storeData?.ShopID,
          cart: updatedCart,
          orderId: res.data.PurchaseToken,
          status: res?.status,
        });

        handleEventLogger({
          eventType: 'payment',
          shopifyDetails: {
            shopId: storeData?.ShopID,
          },
          apiDetails: {
            payload: input,
            response: res.data,
            message: 'Payment Successfull',
          },
          eventToken: eventToken || null,
          cartDetails: cartData,
        });
      }
      dispatch(
        setCartData({
          ...cartData,
          Email: setCartDataRef?.current?.Email || cartData.Email,
          ShippingAddress:
            setCartDataRef?.current?.ShippingAddress ||
            cartData.ShippingAddress,
          PhoneNumber:
            setCartDataRef?.current?.PhoneNumber || cartData.PhoneNumber,
          BillingAddress: payload.BillingAddress,
        }),
      );
      dispatch(setPurchaseToken(res?.data?.PurchaseToken));
      dispatch(setCheckoutOrderID(res?.data?.OrderID));
      dispatch(resetState());
      dispatch(setPurchaseCompleted(true));
      dispatch(setIsPaymentByPaypal(true));
      storePhoneCode('+1');
      if (postUpSellProducts?.length) {
        navigate(postUpsellRedirectUrl);
      } else {
        navigate(redirectUrl);
      }

      let upselldataParsed = '';
      try {
        upselldataParsed = JSON.stringify(upsellData || '{}');
      } catch (error) {
        console.error('Error parsing upsellData:', error);
      }

      if (
        postUpsellPage?.includes(storeData?.ShopID) &&
        postUpSellProducts &&
        postUpSellProducts.length > 0
      ) {
        navigate(postUpsellRedirectUrl);
      } else if (upsellData && upselldataParsed !== '{}') {
        handleNavigate();
      } else {
        dispatch(resetState());
        dispatch(setPurchaseCompleted(true));
        storePhoneCode('+1');
      }
    } catch (error: unknown) {
      const err = error as {
        status: number;
        message: string;
        error: {
          response: {
            data: {
              ProcessMessage: string;
              Msg: string;
              StatusCode: number;
            };
          };
        };
      };
      console.error('err', error);
      handleEventLogger({
        eventType: 'payment',
        shopifyDetails: {
          shopId: storeData?.ShopID,
        },
        apiDetails: {
          payload: input,
          response: error,
          message: 'Payment Failed',
        },
        eventToken: eventToken || null,
        cartDetails: cartData,
      });
      const errorMessage = err?.error?.response?.data?.ProcessMessage
        ? err?.error?.response?.data?.Msg
        : '';
      if (errorMessage) {
        dispatch(setCheckoutErrorMessage(errorMessage));
      } else if (err?.status === 402) {
        setIsDeclinePopup(true);
        setDeclineReasonCode(err?.message || 'Invalid card information');
        dispatch(setSubmitSuccess(false));
      } else if (
        err?.status === 403 &&
        err?.message?.toLowerCase().includes('fraud')
      ) {
        dispatch(setCheckoutErrorMessage(`Order can not be completed`));
      } else {
        setCheckoutErrorMessage(
          err?.message ||
            `We encountered an error. Please try again. ${err?.status}`,
        );
      }
      if (err.status) {
        dispatch(setSubmitSuccess(false));
      }
      setLoading(false);
    } finally {
      dispatch(setSubmitSuccess(false));
    }
  };

  if (!storeData?.IsPaypalEnabled && !getIsViewIsEdit()) return <></>;

  //customization for paypal button

  const windowWidth = useAppSelector(state => state.store.currentWidth);
  const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
  const marginStyles = getMarginStyles(margin, currentDeviceType);

  const containerStyle = {
    ...marginStyles,
    backgroundColor: background?.isInherit ? 'inherit' : background?.color,
    ...getPaddingStyles(padding, currentDeviceType),
    ...getBorderProps(border, currentDeviceType),
  };
  const buttonHeight = buttonCustomization?.height?.[currentDeviceType] || '48';
  const buttonWidth =
    buttonCustomization?.maxWidth?.[currentDeviceType] || '160';
  const buttonHeightNum = Math.min(
    55,
    Math.max(25, parseInt(buttonHeight === 'auto' ? '48' : buttonHeight, 10)),
  );
  const buttonWidthNum = isNaN(+buttonWidth)
    ? buttonWidth
    : Math.min(
        500,
        Math.max(
          150,
          parseInt(buttonWidth === 'auto' ? '160' : buttonWidth, 10),
        ),
      );

  const paypalButtonStyle = {
    height: buttonHeightNum,
    'max-width': buttonWidthNum,
    color: buttonColor,
    shape: 'rect' as const,
    justifyContent: 'justify-center',
    Label: 'paypal' as const,
  };
  useEffect(() => {
    if (termsError && isTermsAccepted) {
      setTermsError(null);
    }
  }, [isTermsAccepted]);

  return (
    <div className="text-editor-content">
      {loading ? (
        <div
          className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center backdrop-blur-sm"
          style={{ zIndex: 1000 }}>
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <PayPalScriptProvider options={initialOptions}>
          <div
            className={`flex flex-col  ${border?.borderStyle?.[currentDeviceType]}`}
            style={containerStyle}>
            <div>
              {!showOnlyButton && (
                <div dangerouslySetInnerHTML={{ __html: headerText }} />
              )}
              <div
                className={`w-fit ${justifyContent}`}
                style={{
                  width: isNaN(+buttonWidthNum)
                    ? buttonWidthNum
                    : `${buttonWidthNum}px`,
                  height: isNaN(+buttonHeightNum)
                    ? buttonHeightNum
                    : `${buttonHeightNum}px`,
                }}>
                {editMode ||
                (!isTermsAccepted && TermsAndConCheck?.isTermsAndConChecked) ? (
                  <button
                    onClick={() => {
                      setTermsError(
                        TermsAndConCheck?.errorMsg ||
                          'Please accept the terms and conditions.',
                      );
                    }}
                    type="button"
                    role="link"
                    aria-label="Pay with PayPal"
                    className={`flex items-center justify-center w-full h-full shadow-sm hover:brightness-95 transition duration-200 ${justifyContent}
            ${buttonColor === 'gold' ? 'bg-[#FFC439]' : ''}
            ${buttonColor === 'silver' ? 'bg-[#EEEEEE]' : ''}
            ${buttonColor === 'black' ? 'bg-[#2C2E2F] text-white' : ''}
            ${buttonColor === 'white' ? 'bg-white border text-black' : ''}
            ${buttonColor === 'blue' ? 'bg-[#0070BA] text-white' : ''}
            rounded`}>
                    <span className="flex items-center">
                      <PayPal color={buttonColor} />
                    </span>
                  </button>
                ) : (
                  <PayPalButtons
                    createOrder={onCreateOrder}
                    onShippingAddressChange={onShippingChange}
                    onApprove={onApproveOrder}
                    fundingSource={FUNDING.PAYPAL}
                    style={{ ...paypalButtonStyle }}
                    disabled={isCartApiLoading}
                  />
                )}
              </div>
              {termsError && (
                <p
                  className={`text-red-500 text-sm mt-2 ${getAlignmentClass(justifyContent)}`}>
                  {termsError}
                </p>
              )}
            </div>
            {!showOnlyButton && (
              <div className="flex items-center w-full pt-4">
                <hr className="flex-grow border-t border-gray-300" />
                <span className="mx-2 text-gray-500 text-sm whitespace-nowrap">
                  OR
                </span>
                <hr className="flex-grow border-t border-gray-300" />
              </div>
            )}
          </div>
        </PayPalScriptProvider>
      )}
    </div>
  );
};
export default PaypalButton;
